import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraDataTable, SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraButton, SpecraInput, SpecraLink, SpecraSelect, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraCombobox, SpecraInlineBanner } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { projectDataClient } from '../../../../lib/connect/clients';
import { parseWorkbookMapping, usePriceDocumentMetadata } from './usePriceVolume';
import { usePricingWrite } from './usePricingRateBook';
const T = Testids.pricingWorkspace;
export function ConnectWorkbookDialog({ scope, documents, categories, periods, addSpreadsheetHref, workbookId, initialDocumentId, onClose }) {
    const cache = useQueryClient();
    const [documentId, setDocumentId] = useState(initialDocumentId || '');
    const doc = documents.find((d)=>d.volumeDocumentId === documentId);
    const metadata = usePriceDocumentMetadata(scope.projectId, doc, scope.principal);
    const [mapping, setMapping] = useState();
    const [sheets, setSheets] = useState([]);
    const [manual, setManual] = useState(false);
    const [finding, setFinding] = useState(false);
    const [error, setError] = useState('');
    const generation = useRef(0);
    const busy = useRef(false);
    const newId = useRef(`pwb_${crypto.randomUUID()}`);
    const replaced = useRef(false);
    const close = ()=>{
        generation.current++;
        onClose();
        if (replaced.current) void cache.invalidateQueries({
            queryKey: [
                'pricing-evidence'
            ]
        });
    };
    const save = usePricingWrite(async ()=>{
        if (!doc || !mapping || !metadata.data?.document?.currentVersionId) throw new Error('Current document version unavailable. Retry metadata.');
        if (workbookId && !replaced.current) {
            await projectDataClient.unbindPricingWorkbook({
                projectId: scope.projectId,
                workbookId
            });
            replaced.current = true;
        }
        await projectDataClient.registerPricingWorkbook({
            projectId: scope.projectId,
            pricingId: scope.scenarioId,
            workbookId: newId.current,
            bindingKind: 'volume_document',
            shirohigeDocumentId: doc.shirohigeDocumentId,
            volumeDocumentId: doc.volumeDocumentId,
            artifactId: doc.shirohigeDocumentId,
            workbookVersionId: metadata.data.document.currentVersionId,
            mappingJson: JSON.stringify({
                ...mapping,
                unmatched_template_rows: mapping.unmatched_template_rows.filter((r)=>!mapping.rows.some((m)=>m.row === r.row && m.category_id)),
                unmatched_categories: categories.filter((c)=>!mapping.rows.some((r)=>r.category_id === c.id)).map((c)=>c.id)
            })
        });
    });
    const updateRow = (index, update)=>setMapping((m)=>m && {
                ...m,
                rows: m.rows.map((r, i)=>i === index ? {
                        ...r,
                        ...update
                    } : r)
            });
    const validRef = (ref)=>/^[A-Z]{1,3}[0-9]{1,6}$/.test(ref) && !mapping?.protected_cells?.includes(ref) && !mapping?.protected_columns?.includes(ref.replace(/[0-9]+$/, ''));
    const invalid = !mapping?.sheet || !mapping.rows.length || mapping.rows.some((r)=>!r.category_id || !categories.some((c)=>c.id === r.category_id) || !r.title.trim() || !validRef(r.title_cell) || !Object.values(r.cells).some((c)=>c.rate || c.hours) || Object.values(r.cells).some((c)=>Object.values(c).some((ref)=>!!ref && !validRef(ref))));
    const find = async ()=>{
        if (!doc || finding || busy.current) return;
        const request = ++generation.current;
        setFinding(true);
        setError('');
        setMapping(undefined);
        try {
            const { proposal } = await projectDataClient.proposePricingWorkbookMapping({
                projectId: scope.projectId,
                scenarioId: scope.scenarioId,
                volumeDocumentId: doc.volumeDocumentId
            });
            if (request !== generation.current) return;
            if (!proposal) throw new Error('No mapping returned. Retry Find columns.');
            const next = parseWorkbookMapping(proposal.mappingJson);
            const unmatched = next.unmatched_template_rows.map((r)=>({
                    category_id: '',
                    lcat_logical_key: '',
                    row: r.row,
                    title_cell: '',
                    title: r.title,
                    cells: {}
                }));
            setMapping({
                ...next,
                rows: [
                    ...next.rows,
                    ...unmatched
                ]
            });
            setSheets(proposal.sheetNames);
            setManual(proposal.status === 'manual');
        } catch (e) {
            if (request === generation.current) setError(e.message);
        } finally{
            if (request === generation.current) setFinding(false);
        }
    };
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: true,
        size: "xl",
        title: workbookId ? 'Change mapping' : 'Connect workbook',
        "data-testid": T.priceVolumeDialog,
        onOpenChange: (open)=>{
            if (!open && !busy.current) {
                close();
            }
        },
        footer: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "secondary",
                    disabled: save.isPending,
                    onClick: ()=>{
                        close();
                    },
                    children: "Cancel"
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": T.priceVolumeSave,
                    disabled: !!invalid || finding || save.isPending || !metadata.data?.document?.currentVersionId || metadata.isFetching || metadata.isError,
                    onClick: async ()=>{
                        if (busy.current) return;
                        busy.current = true;
                        try {
                            await save.mutateAsync();
                            onClose();
                        } catch  {
                        /* inline */ } finally{
                            busy.current = false;
                        }
                    },
                    children: save.isPending ? 'Connecting…' : 'Connect'
                })
            ]
        }),
        children: /*#__PURE__*/ _jsxs("fieldset", {
            disabled: save.isPending,
            children: [
                !documents.length ? /*#__PURE__*/ _jsx(SpecraLink, {
                    href: addSpreadsheetHref,
                    children: "Add a spreadsheet to a volume"
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsxs(SpecraSelect, {
                            "aria-label": "Spreadsheet",
                            "data-testid": T.priceVolumeDocument,
                            value: documentId,
                            disabled: save.isPending || !!workbookId,
                            onChange: (e)=>{
                                generation.current++;
                                setDocumentId(e.target.value);
                                setMapping(undefined);
                                setError('');
                                setFinding(false);
                                setSheets([]);
                            },
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "",
                                    children: "Choose a spreadsheet"
                                }),
                                documents.map((d)=>/*#__PURE__*/ _jsx("option", {
                                        value: d.volumeDocumentId,
                                        children: d.label || d.submissionFilename || 'Spreadsheet'
                                    }, d.volumeDocumentId))
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": T.priceVolumeFind,
                            disabled: !doc || finding || save.isPending,
                            onClick: find,
                            children: finding ? 'Finding columns…' : 'Find columns'
                        })
                    ]
                }),
                doc && metadata.isPending && /*#__PURE__*/ _jsx(SpecraText, {
                    children: "Loading document version…"
                }),
                doc && (metadata.isError || !metadata.isPending && !metadata.data?.document?.currentVersionId) && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                    tone: "danger",
                    children: [
                        "Current version unavailable.",
                        ' ',
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            onClick: ()=>metadata.refetch(),
                            children: "Retry metadata"
                        })
                    ]
                }),
                workbookId && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "warning",
                    children: "Connecting replaces the current binding. If reconnecting fails after disconnecting, retry Connect to restore the connection."
                }),
                (error || save.error) && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                    tone: "danger",
                    children: [
                        replaced.current && save.error ? 'The previous binding was disconnected. Retry Connect. ' : '',
                        error || save.error?.message
                    ]
                }),
                mapping && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        manual && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                            tone: "warning",
                            children: "Columns could not be detected. Choose a sheet and add the template rows manually."
                        }),
                        manual && /*#__PURE__*/ _jsxs(SpecraSelect, {
                            "aria-label": "Sheet",
                            "data-testid": T.priceVolumeSheet,
                            value: mapping.sheet,
                            onChange: (e)=>setMapping({
                                    ...mapping,
                                    sheet: e.target.value
                                }),
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "",
                                    children: "Choose a sheet"
                                }),
                                sheets.map((s)=>/*#__PURE__*/ _jsx("option", {
                                        children: s
                                    }, s))
                            ]
                        }),
                        !!(mapping.unmatched_template_rows.length || mapping.unmatched_categories.length || mapping.unmatched_periods.length) && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                            tone: "warning",
                            children: [
                                "Unmatched template rows:",
                                ' ',
                                mapping.unmatched_template_rows.map((r)=>`${r.row}: ${r.title}`).join(', ') || 'none',
                                ". Unmatched categories:",
                                ' ',
                                categories.filter((c)=>!mapping.rows.some((r)=>r.category_id === c.id)).map((c)=>c.title).join(', ') || 'none',
                                ". Unmatched periods: ",
                                mapping.unmatched_periods.join(', ') || 'none',
                                "."
                            ]
                        }),
                        mapping.extractor_warnings.map((warning, i)=>/*#__PURE__*/ _jsx(SpecraInlineBanner, {
                                tone: "warning",
                                children: warning
                            }, `${i}-${warning}`)),
                        /*#__PURE__*/ _jsx(SpecraText, {
                            children: "Use cell references such as E7. Protected formula cells cannot be mapped. Remove any template rows you do not want to connect."
                        }),
                        /*#__PURE__*/ _jsx(SpecraDataTable, {
                            testId: T.priceVolumeMapping,
                            rows: mapping.rows,
                            getRowId: (r)=>mapping.rows.indexOf(r),
                            columns: [
                                {
                                    id: 'title',
                                    header: 'Template row title',
                                    cell: (r)=>/*#__PURE__*/ _jsx(SpecraInput, {
                                            "aria-label": `Template title ${mapping.rows.indexOf(r) + 1}`,
                                            value: r.title,
                                            disabled: !manual,
                                            onChange: (e)=>updateRow(mapping.rows.indexOf(r), {
                                                    title: e.target.value
                                                })
                                        })
                                },
                                {
                                    id: 'category',
                                    header: 'Our category',
                                    cell: (r)=>/*#__PURE__*/ _jsx(SpecraCombobox, {
                                            label: `Our category ${mapping.rows.indexOf(r) + 1}`,
                                            value: r.category_id,
                                            options: categories.map((c)=>({
                                                    value: c.id,
                                                    label: c.title
                                                })),
                                            onValueChange: (id)=>updateRow(mapping.rows.indexOf(r), {
                                                    category_id: id || '',
                                                    lcat_logical_key: categories.find((c)=>c.id === id)?.lcatLogicalKey || ''
                                                })
                                        })
                                },
                                {
                                    id: 'titleCell',
                                    header: 'Title cell',
                                    cell: (r)=>/*#__PURE__*/ _jsx(SpecraInput, {
                                            "aria-label": `Title cell ${mapping.rows.indexOf(r) + 1}`,
                                            value: r.title_cell,
                                            invalid: !validRef(r.title_cell),
                                            onChange: (e)=>updateRow(mapping.rows.indexOf(r), {
                                                    title_cell: e.target.value,
                                                    row: Number(e.target.value.match(/[0-9]+$/)?.[0] || 0)
                                                })
                                        })
                                },
                                ...periods.flatMap((p)=>[
                                        'rate',
                                        'hours'
                                    ].map((role)=>({
                                            id: `${p.periodKey}-${role}`,
                                            header: `${p.label} ${role} cell`,
                                            cell: (r)=>/*#__PURE__*/ _jsx(SpecraInput, {
                                                    "data-testid": T.priceVolumeCell,
                                                    "aria-label": `${p.label} ${role} cell ${mapping.rows.indexOf(r) + 1}`,
                                                    value: r.cells[p.periodKey]?.[role] || '',
                                                    invalid: !!r.cells[p.periodKey]?.[role] && !validRef(r.cells[p.periodKey][role] || ''),
                                                    onChange: (e)=>updateRow(mapping.rows.indexOf(r), {
                                                            cells: {
                                                                ...r.cells,
                                                                [p.periodKey]: {
                                                                    ...r.cells[p.periodKey],
                                                                    [role]: e.target.value
                                                                }
                                                            }
                                                        })
                                                })
                                        }))),
                                {
                                    id: 'remove',
                                    header: 'Remove',
                                    cell: (r)=>/*#__PURE__*/ _jsxs(SpecraButton, {
                                            variant: "ghost",
                                            onClick: ()=>setMapping({
                                                    ...mapping,
                                                    rows: mapping.rows.filter((row)=>row !== r)
                                                }),
                                            children: [
                                                "Remove row ",
                                                mapping.rows.indexOf(r) + 1
                                            ]
                                        })
                                }
                            ]
                        }),
                        manual && /*#__PURE__*/ _jsx(SpecraButton, {
                            onClick: ()=>setMapping({
                                    ...mapping,
                                    rows: [
                                        ...mapping.rows,
                                        {
                                            category_id: '',
                                            lcat_logical_key: '',
                                            row: 0,
                                            title_cell: '',
                                            title: '',
                                            cells: {}
                                        }
                                    ]
                                }),
                            children: "Add template row"
                        }),
                        invalid && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                            tone: "warning",
                            children: "Choose a sheet and category for each row, with a valid title cell and at least one rate or hours cell. References must match A1 format and avoid protected cells."
                        })
                    ]
                })
            ]
        })
    });
}
